import { Editor } from '@monaco-editor/react';
import { useState, useEffect, useCallback } from 'react';
import { githubFileGet } from '../../api/github/fileGet';
import { r2FileDelete } from '../../api/r2/fileDelete';
import { r2FileGet } from '../../api/r2/fileGet';
import { r2FileSave } from '../../api/r2/fileSave';
import { vscodeTheme } from '../../styles/theme';
import { useToast } from '../shared/Toast';

interface FileEditorProps {
  bucketId?: string;
  fileKey?: string;
  githubFile?: {
    owner: string;
    repo: string;
    branch: string;
    path: string;
  };
  onFileDeleted?: () => void;
}

export function FileEditor({
  bucketId,
  fileKey,
  githubFile,
  onFileDeleted,
}: FileEditorProps) {
  const { showToast } = useToast();
  const [content, setContent] = useState<string>('');
  const [etag, setEtag] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');

  const isReadOnly = Boolean(githubFile);
  const isR2File = Boolean(bucketId && fileKey);

  const saveFile = useCallback(async () => {
    if (!isR2File) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const data = await r2FileSave({
        bucket_id: bucketId ?? '',
        key: fileKey ?? '',
        content,
        if_match: etag,
      });
      setEtag(data.etag);
      setStatusMessage('Saved!');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save file');
      showToast('Failed to save file', 'error');
    } finally {
      setSaving(false);
    }
  }, [bucketId, fileKey, content, etag, isR2File, showToast]);

  useEffect(() => {
    loadFile();
  }, [
    bucketId,
    fileKey,
    githubFile?.owner,
    githubFile?.repo,
    githubFile?.branch,
    githubFile?.path,
  ]);

  useEffect(() => {
    if (!isR2File) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveFile();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isR2File, saveFile]);

  const loadFile = async () => {
    try {
      setLoading(true);
      setError(null);
      if (githubFile) {
        const data = await githubFileGet({
          owner: githubFile.owner,
          repo: githubFile.repo,
          path: githubFile.path,
          ref: githubFile.branch,
          format: 'raw',
        });
        setContent(data.content);
        setEtag(data.sha);
      } else if (bucketId && fileKey) {
        const data = await r2FileGet({ bucket_id: bucketId, key: fileKey });
        setContent(data.content);
        setEtag(data.etag);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async () => {
    if (!isR2File) {
      return;
    }
    if (!confirm(`Are you sure you want to delete "${fileKey}"?`)) {
      return;
    }
    try {
      setDeleting(true);
      setError(null);
      await r2FileDelete({
        bucket_id: bucketId ?? '',
        key: fileKey ?? '',
        if_match: etag,
      });
      showToast('File deleted');
      if (onFileDeleted) {
        onFileDeleted();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete file');
      showToast('Failed to delete file', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', color: vscodeTheme.colors.textPrimary }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#f48771' }}>Error: {error}</div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '8px 16px',
          borderBottom: `1px solid ${vscodeTheme.colors.border}`,
          backgroundColor: vscodeTheme.colors.headerBackground,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          position: 'relative',
        }}
        id="toast-host"
      >
        <span
          style={{
            color: vscodeTheme.colors.textPrimary,
            fontSize: vscodeTheme.typography.fontSize,
            fontFamily: vscodeTheme.typography.fontFamily,
            flex: 1,
          }}
        >
          {githubFile
            ? `${githubFile.owner}/${githubFile.repo}@${githubFile.branch} / ${githubFile.path}`
            : `${bucketId ?? ''} / ${fileKey ?? ''}`}
        </span>
        {isReadOnly && (
          <span
            style={{
              fontSize: '11px',
              color: vscodeTheme.colors.textSecondary,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            [読み取り専用]
          </span>
        )}
        {statusMessage && (
          <span
            style={{
              color: vscodeTheme.colors.textPrimary,
              fontSize: vscodeTheme.typography.fontSize,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            {statusMessage}
          </span>
        )}
        {!isReadOnly && (
          <>
            <button
              onClick={saveFile}
              disabled={saving}
              style={{
                padding: '6px 10px',
                backgroundColor: 'transparent',
                color: saving
                  ? vscodeTheme.colors.textSecondary
                  : vscodeTheme.colors.textPrimary,
                border: 'none',
                borderRadius: '2px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                transition: 'opacity 0.1s',
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.opacity = '0.7';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving) {
                  e.currentTarget.style.opacity = '1';
                }
              }}
              title="Save"
            >
              {saving ? '⏳' : '💾'}
            </button>
            <button
              onClick={deleteFile}
              disabled={deleting}
              style={{
                padding: '6px 10px',
                backgroundColor: 'transparent',
                color: deleting ? vscodeTheme.colors.textSecondary : '#f48771',
                border: 'none',
                borderRadius: '2px',
                cursor: deleting ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                transition: 'opacity 0.1s',
              }}
              onMouseEnter={(e) => {
                if (!deleting) {
                  e.currentTarget.style.opacity = '0.7';
                }
              }}
              onMouseLeave={(e) => {
                if (!deleting) {
                  e.currentTarget.style.opacity = '1';
                }
              }}
              title="Delete file"
            >
              {deleting ? '⏳' : '🗑️'}
            </button>
          </>
        )}
      </div>
      <Editor
        height="100%"
        defaultLanguage="json"
        value={content}
        onChange={(value) => setContent(value || '')}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          readOnly: isReadOnly,
          bracketPairColorization: { enabled: true },
        }}
      />
    </div>
  );
}
